// Admin-side types for the System Checklist Library (versioned catalog of
// inspection checklists published from the InspectTec Library tenant and
// copied down into client tenants). Mirror the backend ResultModels in
// `Inspect.Admin.Application.Features.Admin.ChecklistLibrary.*` — keep both
// ends in sync if a shape changes. Enums serialise as strings.

// ────────────────────────────────────────────────────────────────────────────
// Enum string values (backend `Inspect.Domain.Enums.*`, JsonStringEnumConverter)
// ────────────────────────────────────────────────────────────────────────────

export const ASSET_TYPES = [
	"Equipment",
	"Vehicle",
	"Site",
	"Building",
	"Inventory",
	"Other",
] as const;

/** The status values a copy-down wires as pass / flag / fail outcomes. */
export const ASSET_STATUSES = [
	"Operational",
	"Functional",
	"InService",
	"Good",
	"InStock",
	"MaintenanceRequired",
	"Fair",
	"NeedsAttention",
	"NeedsService",
	"RequiredFix",
	"LowStock",
	"Bad",
	"Broken",
	"NonOperational",
	"NonFunctional",
	"OutOfService",
	"Decommissioned",
	"Critical",
	"OutOfStock",
	"TaggedOut",
] as const;

export const INSPECTION_TYPES = ["OnDemand", "Scheduled"] as const;

/**
 * `AssetStatusFamily` values a configuration may block on
 * (`InspectionConfigurationBlockingPolicyResolver.SelectableFamilies`).
 */
export const BLOCK_ON_FAMILIES = ["Warning", "Bad"] as const;

// The industry catalog (`GET /industries`) lives in `./industry.ts`.

// ────────────────────────────────────────────────────────────────────────────
// Catalog items
// ────────────────────────────────────────────────────────────────────────────

/** Industry tag as returned by the API. */
export interface LibraryIndustryTag {
	industryKey: string;
	industryName?: string | null;
	includeInStarterPack: boolean;
	displayOrder: number;
}

/** Industry tag as sent to the API (replace-set semantics). */
export interface LibraryIndustryTagInput {
	industryKey: string;
	includeInStarterPack: boolean;
	displayOrder: number;
}

/** One catalog row as listed by `GET /checklist-library`. */
export interface SystemChecklistListItem {
	id: string;
	key: string;
	name: string;
	description?: string | null;
	assetClassKey?: string | null;
	layoutModeKey?: string | null;
	regulatoryReference?: string | null;
	recommendedFrequency?: string | null;
	currentVersion: number;
	isPublished: boolean;
	publishedDate?: string | null;
	isActive: boolean;
	industries: LibraryIndustryTag[];
	/** Tenants holding an explicit grant (industry visibility not counted). */
	grantCount: number;
	sourceLibraryClientId?: string | null;
	sourceLibraryChecklistId?: string | null;
	modifiedDate?: string | null;
}

export interface SystemChecklistListResponse {
	items: SystemChecklistListItem[];
	totalCount: number;
}

/** The wiring defaults a copy-down uses. */
export interface LibraryDefaults {
	defaultAssetType?: string | null;
	defaultAssetGroupName?: string | null;
	defaultPassAssetStatus?: string | null;
	defaultFlagAssetStatus?: string | null;
	defaultFailAssetStatus?: string | null;
	defaultInspectionType?: string | null;
	defaultAllowMultipleSameDay?: boolean | null;
	defaultBlockOnAssetStatusFamilies: string[];
	defaultUserGroupName?: string | null;
}

export interface SystemChecklistVersionSummary {
	version: number;
	contentHash: string;
	changeSummary?: string | null;
	publishedByAdminUserId?: string | null;
	publishedByName?: string | null;
	publishedDate: string;
	/** Null when the stored content could not be parsed (backend `int?`). */
	questionCount: number | null;
	groupCount: number | null;
}

/** `GET /checklist-library/{id}` — item + defaults + versions (newest first). */
export interface SystemChecklistDetail extends SystemChecklistListItem {
	defaults: LibraryDefaults;
	versions: SystemChecklistVersionSummary[];
}

/** `GET /checklist-library/{id}/versions/{version}`. */
export interface SystemChecklistVersionContent {
	systemChecklistId: string;
	key: string;
	version: number;
	contentHash: string;
	changeSummary?: string | null;
	publishedByAdminUserId?: string | null;
	publishedDate: string;
	/** Canonical ChecklistGraph JSON as a string. */
	contentJson: string;
}

/**
 * Metadata fields shared by `PUT /checklist-library/{id}` and
 * `POST /checklist-library/publish-from`. Null/undefined = unchanged.
 */
export interface LibraryMetadataInput {
	name?: string | null;
	description?: string | null;
	assetClassKey?: string | null;
	defaultAssetType?: string | null;
	defaultAssetGroupName?: string | null;
	defaultPassAssetStatus?: string | null;
	defaultFlagAssetStatus?: string | null;
	defaultFailAssetStatus?: string | null;
	defaultInspectionType?: string | null;
	defaultAllowMultipleSameDay?: boolean | null;
	defaultBlockOnAssetStatusFamilies?: string[] | null;
	defaultUserGroupName?: string | null;
	regulatoryReference?: string | null;
	recommendedFrequency?: string | null;
	/** null/undefined = keep the current tags, [] = clear, otherwise replace-set. */
	industries?: LibraryIndustryTagInput[] | null;
}

export interface UpdateSystemChecklistRequest extends LibraryMetadataInput {
	isActive?: boolean | null;
}

/** `PUT` response: item + defaults (no versions). */
export interface UpdateSystemChecklistResponse extends SystemChecklistListItem {
	defaults: LibraryDefaults;
}

/** `POST /checklist-library/{id}/deactivate|reactivate`. */
export interface SetSystemChecklistActiveResponse {
	id: string;
	key: string;
	isActive: boolean;
}

// ────────────────────────────────────────────────────────────────────────────
// Publish from a client checklist
// ────────────────────────────────────────────────────────────────────────────

export interface PublishFromChecklistRequest extends LibraryMetadataInput {
	/** Null → the configured `ChecklistLibrary:LibraryClientId`. */
	sourceClientId?: string | null;
	sourceChecklistId: string;
	/** kebab-case, ≤100 chars — the catalog identity. */
	key: string;
	changeSummary?: string | null;
	allowNonLibrarySource: boolean;
	includeInactive: boolean;
}

export type PublishOutcome = "Created" | "NewVersion" | "NoChange";

export interface PublishFromChecklistResult {
	systemChecklistId: string;
	key: string;
	name: string;
	version: number;
	contentHash: string;
	noChange: boolean;
	outcome: PublishOutcome;
	questionCount: number;
	sourceKeysStamped: number;
	warnings: string[];
}

// ────────────────────────────────────────────────────────────────────────────
// Export / import bundle
// ────────────────────────────────────────────────────────────────────────────

export const CHECKLIST_LIBRARY_BUNDLE_FORMAT = "inspecttec-checklist-library/1";

export interface ChecklistLibraryBundleChecklist extends LibraryDefaults {
	key: string;
	name: string;
	description?: string | null;
	assetClassKey?: string | null;
	layoutModeKey?: string | null;
	regulatoryReference?: string | null;
	recommendedFrequency?: string | null;
	isActive: boolean;
	industries: LibraryIndustryTagInput[];
}

export interface ChecklistLibraryBundleVersion {
	version: number;
	contentHash: string;
	changeSummary?: string | null;
	publishedDate?: string | null;
	/** The checklist content as a JSON object (re-canonicalised on import). */
	content: unknown;
}

export interface ChecklistLibraryBundle {
	format: string;
	exportedAt: string;
	checklist: ChecklistLibraryBundleChecklist;
	versions: ChecklistLibraryBundleVersion[];
}

export interface ChecklistLibraryImportResult {
	systemChecklistId: string;
	key: string;
	created: boolean;
	versionsAdded: number;
	versionsSkipped: number;
	currentVersion: number;
	warnings: string[];
}

// ────────────────────────────────────────────────────────────────────────────
// Client grants + import to client
// ────────────────────────────────────────────────────────────────────────────

export interface ClientChecklistGrantItem {
	grantId?: string | null;
	systemChecklistId: string;
	key: string;
	name: string;
	isPublished: boolean;
	isActive: boolean;
	grantedDate?: string | null;
	/** Visible to the tenant through its industry (no explicit grant needed). */
	viaIndustry: boolean;
}

/** A tenant checklist copied down from a library item (non-deleted). */
export interface ClientImportedCopy {
	systemChecklistId: string;
	checklistId: string;
	checklistName: string;
	/** Library version the copy was taken from. */
	version?: number | null;
	isActive: boolean;
	importedDate?: string | null;
}

export interface ClientChecklistGrantsResponse {
	clientId: string;
	industryKey?: string | null;
	items: ClientChecklistGrantItem[];
	/** Older backends omit it — treat as "unknown", not "none imported". */
	importedCopies?: ClientImportedCopy[];
}

export interface GrantSystemChecklistResult {
	grantId: string;
	clientId: string;
	systemChecklistId: string;
	key: string;
	name: string;
	grantedDate: string;
	/** False when the grant already existed (idempotent). */
	created: boolean;
}

export interface RevokeSystemChecklistResult {
	clientId: string;
	systemChecklistId: string;
	revoked: boolean;
	/** Tenant copies already imported from this item (never touched by a revoke). */
	importedCopies: number;
}

export interface ImportToClientRequest {
	nameOverride?: string | null;
	wireInspection: boolean;
	targetAssetGroupId?: string | null;
	targetUserGroupId?: string | null;
	/** Consent to another copy when the tenant already holds one (else 409). */
	allowDuplicate?: boolean;
}

export interface ImportToClientResult {
	systemChecklistId: string;
	systemChecklistKey: string;
	checklistId: string;
	checklistName: string;
	version: number;
	inspectionConfigurationId?: string | null;
	assetGroupId?: string | null;
	userGroupId?: string | null;
	inspectorGroupId?: string | null;
	questionsCreated: number;
	issueTypesCreated: number;
	notificationRulesCreated: number;
	translationsWritten: number;
	skipped: boolean;
	warnings: string[];
}

// ────────────────────────────────────────────────────────────────────────────
// Tenant checklists (Admin.Api proxy of Client `Checklist/GetAll`, x-tenant-id)
// ────────────────────────────────────────────────────────────────────────────

export interface ClientChecklistListItem {
	id: string;
	checklistName: string;
	checklistType?: string | null;
	assetType?: string | null;
	description?: string | null;
	sourceChecklistId?: string | null;
	sourceChecklistName?: string | null;
	isActive: boolean;
	minutesToTimeout?: number | null;
	questionsCount: number;
	ruleSetsCount: number;
	createdDate: string;
	createdBy?: string | null;
	modifiedDate?: string | null;
	modifiedBy?: string | null;
	sourceSystemChecklistId?: string | null;
	sourceSystemChecklistVersion?: number | null;
	latestSystemVersion?: number | null;
	updateAvailable: boolean;
}

export interface ClientChecklistListResponse {
	items: ClientChecklistListItem[];
	totalCount: number;
	pageNumber: number;
	pageSize: number;
}

/**
 * Every page of a tenant's checklists concatenated (publish-source picker).
 * `items.length < totalCount` only when the page cap was hit.
 */
export interface ClientChecklistCollection {
	items: ClientChecklistListItem[];
	totalCount: number;
}
